import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import {
  ANALYZE_PROPERTY_WORK_ORDERS,
  CLASSIFY_PROPERTY_REVIEWS,
  DECIDE_WORK_ORDER_ANALYSIS_RECOMMENDATION,
  GENERATE_PROPERTY_MANAGER_SUMMARY,
  GET_PROPERTY_COMPLETED_WORK_ORDERS,
  GET_PROPERTY_MANAGER_SUMMARY,
  GET_PROPERTY_PERFORMANCE_DETAIL,
  GET_PROPERTY_WORK_ORDER_ANALYSIS,
  GET_WORK_ORDER_ANALYSIS_DECISIONS,
} from '../graphql/propertyPerformance';
import WorkOrderDetailSlidePanel from '../components/work-orders/WorkOrderDetailSlidePanel';
import OwnerReportEditor from '../components/property-performance/OwnerReportEditor';
import PropertyManagerReportActions from '../components/property-performance/PropertyManagerReportActions';

const formatDate = (value) => value ? new Date(value).toLocaleDateString() : '—';

const formatAnalysisLabel = (value) => value
  ? value.toLowerCase().replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase())
  : '—';

const sentimentClasses = {
  POSITIVE: 'bg-emerald-100 text-emerald-800',
  NEGATIVE: 'bg-red-100 text-red-800',
  MIXED: 'bg-amber-100 text-amber-800',
  NEUTRAL: 'bg-gray-100 text-gray-700',
};

const priorityClasses = {
  URGENT: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  MEDIUM: 'bg-amber-100 text-amber-800',
  LOW: 'bg-sky-100 text-sky-800',
};

const confidenceClasses = {
  HIGH: 'bg-emerald-100 text-emerald-800',
  MEDIUM: 'bg-amber-100 text-amber-800',
  LOW: 'bg-gray-100 text-gray-700',
};

const detailTabGroups = [
  {
    label: null,
    tabs: [{ id: 'main', label: 'Main' }],
  },
  {
    label: 'Work Orders',
    tabs: [
      { id: 'completed', label: 'Completed' },
      { id: 'active', label: 'Open / In-Progress' },
      { id: 'grouped-issues', label: 'Grouped Active Issues' },
      { id: 'recurring-issues', label: 'Recurring Issues' },
      { id: 'stale', label: 'Stale' },
    ],
  },
  {
    label: null,
    tabs: [{ id: 'reviews', label: 'Reviews' }],
  },
  {
    label: 'Reporting Summary',
    tabs: [
      { id: 'property-manager', label: 'Property Manager' },
      { id: 'owner', label: 'Owner' },
    ],
  },
];

const WorkOrderLinks = ({ ids, orderMap, onOpen }) => {
  if (!ids?.length) return <span className="text-xs text-gray-400">No linked work orders</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {ids.map(id => (
        <button
          type="button"
          key={id}
          onClick={() => onOpen(id)}
          title={orderMap.get(id)?.description || 'Open work order'}
          className="rounded bg-gray-100 px-2 py-1 text-left text-xs font-medium text-sky-700 hover:bg-sky-100"
        >
          {formatDate(orderMap.get(id)?.completedAt || orderMap.get(id)?.createdAt)} · {orderMap.get(id)?.description?.slice(0, 42) || `Work order ${id.slice(0, 8)}`}
          {orderMap.get(id)?.description?.length > 42 ? '…' : ''}
        </button>
      ))}
    </div>
  );
};

const WorkOrderTable = ({ orders, staleIds = new Set(), completed = false, onOpen }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50"><tr>
        {['Description', 'Type', 'Status', completed ? 'Completed' : 'Created', 'Assignment', ''].map(label => <th key={label} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</th>)}
      </tr></thead>
      <tbody className="divide-y divide-gray-100">
        {orders.map(order => (
          <tr key={order.id} className={staleIds.has(order.id) ? 'bg-amber-50' : ''}>
            <td className="max-w-xl px-4 py-4 text-sm text-gray-900">
              <div className="flex flex-wrap items-center gap-2">
                {order.isUrgent && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Urgent</span>}
                {staleIds.has(order.id) && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Review closure</span>}
              </div>
              <div className="mt-1 whitespace-pre-wrap">{order.description}</div>
            </td>
            <td className="px-4 py-4 text-sm text-gray-600">{order.workOrderType?.replaceAll('_', ' ')}</td>
            <td className="px-4 py-4 text-sm text-gray-600">{order.status?.replaceAll('_', ' ')}</td>
            <td className="px-4 py-4 text-sm text-gray-600">
              <button type="button" onClick={() => onOpen(order.id)} className="font-medium text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-900">
                {formatDate(completed ? order.completedAt : order.createdAt)}
              </button>
            </td>
            <td className="px-4 py-4 text-sm text-gray-600">{order.assignedTo ? `${order.assignedTo.firstName || ''} ${order.assignedTo.lastName || ''}`.trim() : order.assignedRoles?.join(', ') || 'Unassigned'}</td>
            <td className="px-4 py-4 text-right"><button type="button" onClick={() => onOpen(order.id)} className="text-sm font-medium text-sky-700 hover:text-sky-900">Open</button></td>
          </tr>
        ))}
        {!orders.length && <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No work orders in this section.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function PropertyPerformanceDetail() {
  const { propertyId } = useParams();
  const [activeTab, setActiveTab] = useState('main');
  const tabScrollRef = useRef(null);
  const [showMoreTabsArrow, setShowMoreTabsArrow] = useState(false);
  const [completedDays, setCompletedDays] = useState(120);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState('');
  const [generatedWorkOrderAnalysis, setGeneratedWorkOrderAnalysis] = useState(null);
  const [workOrderAnalysisError, setWorkOrderAnalysisError] = useState('');
  const [decisionForm, setDecisionForm] = useState(null);
  const [decisionError, setDecisionError] = useState('');
  const [generatedManagerSummary, setGeneratedManagerSummary] = useState(null);
  const [managerSummaryError, setManagerSummaryError] = useState('');
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [isWorkOrderPanelOpen, setIsWorkOrderPanelOpen] = useState(false);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedLimit, setCompletedLimit] = useState(25);
  const [completedSearch, setCompletedSearch] = useState('');
  const [completedWorkLoaded, setCompletedWorkLoaded] = useState(false);
  const { data, loading, error, refetch } = useQuery(GET_PROPERTY_PERFORMANCE_DETAIL, {
    variables: { propertyId, input: { completedDays } },
    fetchPolicy: 'network-only',
  });
  const detail = data?.propertyPerformanceDetail;
  const detailPropertyId = detail?.property?.id;
  const staleIds = new Set(detail?.staleCandidateIds || []);
  const [classifyReviews, { loading: classifyingReviews }] = useMutation(CLASSIFY_PROPERTY_REVIEWS);
  const {
    data: storedWorkOrderAnalysisData,
    loading: loadingWorkOrderAnalysis,
    error: storedWorkOrderAnalysisError,
    refetch: refetchWorkOrderAnalysis,
  } = useQuery(GET_PROPERTY_WORK_ORDER_ANALYSIS, {
    variables: { propertyId },
    fetchPolicy: 'network-only',
  });
  const [analyzeWorkOrders, { loading: analyzingWorkOrders }] = useMutation(ANALYZE_PROPERTY_WORK_ORDERS);
  const workOrderAnalysis = generatedWorkOrderAnalysis || storedWorkOrderAnalysisData?.propertyWorkOrderAnalysis;
  const {
    data: decisionData,
    error: decisionsError,
    refetch: refetchDecisions,
  } = useQuery(GET_WORK_ORDER_ANALYSIS_DECISIONS, {
    variables: { analysisId: workOrderAnalysis?.id || '' },
    skip: !workOrderAnalysis?.id,
    fetchPolicy: 'network-only',
  });
  const [decideRecommendation, { loading: applyingDecision }] = useMutation(DECIDE_WORK_ORDER_ANALYSIS_RECOMMENDATION);
  const decisions = decisionData?.workOrderAnalysisDecisions || [];
  const decisionByWorkOrder = new Map();
  decisions.forEach(decision => {
    if (!decisionByWorkOrder.has(decision.workOrderId)) decisionByWorkOrder.set(decision.workOrderId, decision);
  });
  const {
    data: storedManagerSummaryData,
    loading: loadingManagerSummary,
    error: storedManagerSummaryError,
    refetch: refetchManagerSummary,
  } = useQuery(GET_PROPERTY_MANAGER_SUMMARY, {
    variables: { propertyId },
    fetchPolicy: 'network-only',
  });
  const [generateManagerSummary, { loading: generatingManagerSummary }] = useMutation(GENERATE_PROPERTY_MANAGER_SUMMARY);
  const managerSummary = generatedManagerSummary || storedManagerSummaryData?.propertyManagerSummary;
  const [fetchCompletedWorkOrders, {
    data: completedWorkData,
    loading: loadingCompletedWork,
    error: completedWorkError,
  }] = useLazyQuery(GET_PROPERTY_COMPLETED_WORK_ORDERS, { fetchPolicy: 'network-only' });
  const completedWork = completedWorkData?.propertyCompletedWorkOrders;

  useEffect(() => {
    const tabScroller = tabScrollRef.current;
    if (!tabScroller) return undefined;
    const updateOverflowIndicator = () => {
      const remainingScroll = tabScroller.scrollWidth - tabScroller.clientWidth - tabScroller.scrollLeft;
      setShowMoreTabsArrow(remainingScroll > 4);
    };
    updateOverflowIndicator();
    tabScroller.addEventListener('scroll', updateOverflowIndicator, { passive: true });
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateOverflowIndicator)
      : null;
    resizeObserver?.observe(tabScroller);
    window.addEventListener('resize', updateOverflowIndicator);
    return () => {
      tabScroller.removeEventListener('scroll', updateOverflowIndicator);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOverflowIndicator);
    };
  }, [detailPropertyId]);

  const openWorkOrderPanel = workOrderId => {
    setSelectedWorkOrderId(workOrderId);
    setIsWorkOrderPanelOpen(true);
  };

  const closeWorkOrderPanel = () => {
    setIsWorkOrderPanelOpen(false);
    setSelectedWorkOrderId(null);
  };

  const loadCompletedWork = async ({
    page = completedPage,
    limit = completedLimit,
    days = completedDays,
    search = completedSearch,
  } = {}) => {
    setCompletedWorkLoaded(true);
    await fetchCompletedWorkOrders({
      variables: {
        propertyId,
        input: { completedDays: days, page, limit, search: search.trim() || undefined },
      },
    });
  };

  const handleClassifyReviews = async (force = false) => {
    setAnalysisError('');
    setAnalysisResult(null);
    try {
      const result = await classifyReviews({
        variables: { propertyId, limit: 20, force },
      });
      setAnalysisResult(result.data.classifyPropertyReviews);
      await refetch();
    } catch (classificationError) {
      setAnalysisError(classificationError.message || 'Unable to analyze reviews');
    }
  };

  const handleAnalyzeWorkOrders = async (force = false) => {
    setWorkOrderAnalysisError('');
    try {
      const result = await analyzeWorkOrders({ variables: { propertyId, force } });
      setGeneratedWorkOrderAnalysis(result.data.analyzePropertyWorkOrders);
      setDecisionForm(null);
      await refetchWorkOrderAnalysis();
    } catch (analysisFailure) {
      setWorkOrderAnalysisError(analysisFailure.message || 'Unable to analyze work orders');
    }
  };

  const openDecisionForm = (workOrderId, action) => {
    setDecisionError('');
    setDecisionForm({ workOrderId, action, note: '', duplicateOfWorkOrderId: '' });
  };

  const handleDecision = async event => {
    event.preventDefault();
    if (!decisionForm?.note.trim() || decisionForm.note.trim().length < 5) {
      setDecisionError('Please enter a manager note of at least 5 characters.');
      return;
    }
    if (decisionForm.action === 'CLOSE_AS_DUPLICATE' && !decisionForm.duplicateOfWorkOrderId) {
      setDecisionError('Select the canonical work order that should remain on record.');
      return;
    }
    if (decisionForm.action !== 'KEEP_OPEN') {
      const confirmed = window.confirm(
        decisionForm.action === 'CLOSE_AS_DUPLICATE'
          ? 'Close this work order as a duplicate? The selected canonical ticket will remain unchanged.'
          : 'Confirm that this issue has been verified as resolved and close the work order?',
      );
      if (!confirmed) return;
    }

    setDecisionError('');
    try {
      await decideRecommendation({
        variables: {
          input: {
            analysisId: workOrderAnalysis.id,
            workOrderId: decisionForm.workOrderId,
            action: decisionForm.action,
            note: decisionForm.note.trim(),
            ...(decisionForm.action === 'CLOSE_AS_DUPLICATE'
              ? { duplicateOfWorkOrderId: decisionForm.duplicateOfWorkOrderId }
              : {}),
          },
        },
      });
      setDecisionForm(null);
      setGeneratedWorkOrderAnalysis(null);
      await Promise.all([refetch(), refetchWorkOrderAnalysis(), refetchDecisions()]);
    } catch (decisionFailure) {
      setDecisionError(decisionFailure.message || 'Unable to apply manager decision');
    }
  };

  const handleGenerateManagerSummary = async (force = false) => {
    setManagerSummaryError('');
    try {
      const result = await generateManagerSummary({ variables: { propertyId, force } });
      setGeneratedManagerSummary(result.data.generatePropertyManagerSummary);
      await refetchManagerSummary();
    } catch (summaryFailure) {
      setManagerSummaryError(summaryFailure.message || 'Unable to generate the property-manager summary');
    }
  };

  const handleWorkOrderDataChange = async () => {
    await Promise.all([
      refetch(),
      refetchWorkOrderAnalysis(),
      refetchManagerSummary(),
      completedWorkLoaded ? loadCompletedWork() : Promise.resolve(),
    ]);
  };

  if (loading && !detail) return <div className="rounded-lg bg-white p-8 text-center text-gray-600">Loading property scorecard…</div>;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">Unable to load scorecard: {error.message}</div>;
  if (!detail) return null;

  const orderMap = new Map(
    [
      ...detail.openWorkOrders,
      ...(completedWork?.items || []),
      ...(workOrderAnalysis?.workOrderReferences || []),
      ...(managerSummary?.workOrderReferences || []),
    ].map(order => [order.id, order]),
  );
  const tabCounts = {
    completed: detail.completedWorkOrderCount,
    active: detail.openWorkOrders.length,
    'grouped-issues': workOrderAnalysis?.issueGroups?.length,
    'recurring-issues': workOrderAnalysis?.recurringIssues?.length,
    stale: workOrderAnalysis?.closureCandidates?.length ?? staleIds.size,
    reviews: detail.recentReviews.length,
  };

  return (
    <div className="mx-auto max-w-7xl py-4">
      <Link to="/dashboard/reports/property-performance" className="text-sm font-medium text-sky-300 lg:text-sky-700">← Property Performance</Link>
      <div className="mt-3 flex flex-col gap-4 rounded-lg bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div><h1 className="text-2xl font-semibold text-gray-900">{detail.property.name}</h1><p className="mt-1 text-sm text-gray-600">{detail.property.address}, {detail.property.city} {detail.property.state}</p></div>
        <div className="rounded-xl border border-sky-200 bg-sky-50 px-6 py-3 text-center"><div className="text-xs font-semibold uppercase tracking-wide text-sky-700">Overall health</div><div className="text-3xl font-bold text-sky-900">{detail.healthScore}</div></div>
      </div>

      <nav aria-label="Property performance sections" className="mt-4 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <label className="block xl:hidden">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">View section</span>
          <div className="relative">
            <select
              value={activeTab}
              onChange={event => setActiveTab(event.target.value)}
              className="block w-full appearance-none rounded-md border border-gray-300 bg-white py-3 pl-3 pr-10 text-base font-medium text-gray-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            >
              {detailTabGroups.map(group => (
                <optgroup key={group.label || 'Overview'} label={group.label || 'Overview'}>
                  {group.tabs.map(tab => {
                    const count = tabCounts[tab.id];
                    return <option key={tab.id} value={tab.id}>{tab.label}{count != null ? ` (${count})` : ''}</option>;
                  })}
                </optgroup>
              ))}
            </select>
            <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-lg text-sky-700">⌄</span>
          </div>
        </label>

        <div className="relative hidden xl:block">
          <div ref={tabScrollRef} className="flex gap-3 overflow-x-auto pb-1 pr-12" role="tablist" aria-orientation="horizontal">
          {detailTabGroups.map((group, groupIndex) => (
            <div key={group.label || group.tabs[0].id} className={`flex shrink-0 flex-col gap-1 ${groupIndex ? 'border-l border-gray-200 pl-3' : ''}`}>
              <div className="h-4 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{group.label || <span className="sr-only">Overview</span>}</div>
              <div className="flex gap-1.5">
                {group.tabs.map(tab => {
                  const isActive = activeTab === tab.id;
                  const count = tabCounts[tab.id];
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 ${isActive ? 'bg-sky-700 text-white shadow-sm' : 'bg-gray-50 text-gray-700 hover:bg-sky-50 hover:text-sky-800'}`}
                    >
                      {tab.label}
                      {count != null && <span className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          </div>
          {showMoreTabsArrow && (
            <>
              <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white via-white/95 to-transparent" />
              <button
                type="button"
                aria-label="Show more report sections"
                title="More sections"
                onClick={() => tabScrollRef.current?.scrollBy({ left: Math.max(260, tabScrollRef.current.clientWidth * 0.55), behavior: 'smooth' })}
                className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-sky-200 bg-white text-2xl font-semibold text-sky-700 shadow-md hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1"
              >
                →
              </button>
            </>
          )}
        </div>
      </nav>

      {activeTab === 'main' && <>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Maintenance score</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{detail.maintenanceScore}</div>
          <p className="mt-1 text-xs text-gray-500">Based on active work orders, urgency, age, and overdue status.</p>
        </div>
        <div className="rounded-lg bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Review adjustment</div>
          <div className={`mt-1 text-2xl font-bold ${detail.reviewScoreAdjustment < 0 ? 'text-red-700' : detail.reviewScoreAdjustment > 0 ? 'text-emerald-700' : 'text-gray-900'}`}>{detail.reviewScoreAdjustment > 0 ? '+' : ''}{Number(detail.reviewScoreAdjustment).toFixed(2)}</div>
          <p className="mt-1 text-xs text-gray-500">Newer property-related reviews receive more weight. Adjustment is capped from −20 to +3.</p>
        </div>
        <div className="rounded-lg bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Review coverage</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{detail.classifiedReviewCount}/{detail.recentReviews.length}</div>
          <p className="mt-1 text-xs text-gray-500">{detail.propertyRelatedReviewCount} property-related · {detail.excludedReviewCount} excluded · {detail.unclassifiedReviewCount} pending</p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg bg-white p-5 shadow-sm lg:col-span-2"><h2 className="font-semibold text-gray-900">Why this score</h2><ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">{detail.scoreExplanation.map(item => <li key={item}>{item}</li>)}</ul></div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5"><div className="text-sm font-semibold text-amber-900">Closure review candidates</div><div className="mt-2 text-3xl font-bold text-amber-900">{staleIds.size}</div><p className="mt-1 text-xs text-amber-800">Nonurgent active work at least 45 days old. These are flags only and remain part of the score.</p></div>
      </div>
      </>}

      {activeTab === 'property-manager' && <section className="mt-4 rounded-lg border border-sky-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Property manager briefing</h2>
            <p className="mt-1 text-sm text-gray-500">An internal operational handoff using all active work, the last 120 days of completed work, classified reviews, and recorded manager decisions.</p>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            <button
              type="button"
              onClick={() => handleGenerateManagerSummary(Boolean(managerSummary))}
              disabled={generatingManagerSummary || loadingManagerSummary}
              className="whitespace-nowrap rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {generatingManagerSummary ? 'Generating briefing…' : managerSummary ? 'Regenerate briefing' : 'Generate manager briefing'}
            </button>
            <PropertyManagerReportActions
              propertyId={propertyId}
              propertyName={detail.property.name}
              summary={managerSummary}
            />
          </div>
        </div>

        {(managerSummaryError || storedManagerSummaryError) && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{managerSummaryError || storedManagerSummaryError?.message}</div>
        )}
        {!loadingManagerSummary && !managerSummary && !managerSummaryError && !storedManagerSummaryError && (
          <div className="mt-4 rounded-lg bg-sky-50 p-4 text-sm text-sky-900">No internal briefing has been generated for this property.</div>
        )}

        {managerSummary && (
          <div className="mt-5 space-y-5">
            {!managerSummary.isCurrent && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Property data has changed since this briefing was generated. Regenerate it before using it for a handoff.</div>
            )}
            {managerSummary.reused && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">No source data changed, so the saved briefing was reused without another AI request.</div>
            )}
            <div className="rounded-lg bg-slate-800 p-5 text-white">
              <div className="text-xs font-semibold uppercase tracking-wide text-sky-200">Internal executive overview</div>
              <p className="mt-2 text-sm leading-6 text-slate-100">{managerSummary.executiveOverview}</p>
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
              {[
                ['Immediate priorities', managerSummary.immediatePriorities, 'border-red-200 bg-red-50'],
                ['Completed work highlights', managerSummary.completedWorkHighlights, 'border-emerald-200 bg-emerald-50'],
                ['Watch items', managerSummary.watchItems, 'border-amber-200 bg-amber-50'],
              ].map(([title, items, classes]) => (
                <div key={title}>
                  <h3 className="font-semibold text-gray-900">{title}</h3>
                  <div className="mt-3 space-y-3">
                    {items.map((item, index) => (
                      <article key={`${title}-${item.title}-${index}`} className={`rounded-lg border p-4 ${classes}`}>
                        <h4 className="text-sm font-semibold text-gray-900">{item.title}</h4>
                        <p className="mt-1 text-sm text-gray-700">{item.detail}</p>
                        <div className="mt-3"><WorkOrderLinks ids={item.workOrderIds} orderMap={orderMap} onOpen={openWorkOrderPanel} /></div>
                      </article>
                    ))}
                    {!items.length && <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">Nothing to report in this section.</p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900">Guest feedback themes</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-700">
                  {managerSummary.guestFeedbackThemes.map((theme, index) => <li key={`${theme}-${index}`}>{theme}</li>)}
                  {!managerSummary.guestFeedbackThemes.length && <li className="list-none text-gray-500">No property-related guest theme was identified.</li>}
                </ul>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900">Manager handoff notes</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-700">
                  {managerSummary.managerHandoffNotes.map((note, index) => <li key={`${note}-${index}`}>{note}</li>)}
                  {!managerSummary.managerHandoffNotes.length && <li className="list-none text-gray-500">No additional handoff note was generated.</li>}
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3 text-xs text-gray-400">Generated {new Date(managerSummary.generatedAt).toLocaleString()} with {managerSummary.model}. Completed-work window: {managerSummary.completedDays} days. Usage: {managerSummary.inputTokens} input / {managerSummary.outputTokens} output tokens.</div>
          </div>
        )}
      </section>}

      {['grouped-issues', 'recurring-issues', 'stale'].includes(activeTab) && <section className="mt-4 rounded-lg border border-violet-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">AI work-order intelligence</h2>
            <p className="mt-1 text-sm text-gray-500">Groups related work, finds recurring issues, and suggests tickets for human closure review. It never changes a work order.</p>
          </div>
          <button
            type="button"
            onClick={() => handleAnalyzeWorkOrders(Boolean(workOrderAnalysis))}
            disabled={analyzingWorkOrders || loadingWorkOrderAnalysis}
            className="whitespace-nowrap rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {analyzingWorkOrders ? 'Analyzing work orders…' : workOrderAnalysis ? 'Regenerate analysis' : 'Analyze work orders with AI'}
          </button>
        </div>

        {(workOrderAnalysisError || storedWorkOrderAnalysisError || decisionsError) && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {workOrderAnalysisError || storedWorkOrderAnalysisError?.message || decisionsError?.message}
          </div>
        )}
        {!loadingWorkOrderAnalysis && !workOrderAnalysis && !workOrderAnalysisError && !storedWorkOrderAnalysisError && (
          <div className="mt-4 rounded-lg bg-violet-50 p-4 text-sm text-violet-900">No AI work-order analysis has been generated for this property. Run it when you are ready to review this property.</div>
        )}

        {workOrderAnalysis && (
          <div className="mt-5 space-y-5">
            {!workOrderAnalysis.isCurrent && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Work orders have changed since this analysis was generated. Regenerate it before relying on the recommendations.</div>
            )}
            {workOrderAnalysis.reused && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">Nothing changed, so the current saved analysis was reused without another AI request.</div>
            )}
            <div className="rounded-lg bg-violet-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">Property summary</div>
              <p className="mt-2 text-sm text-violet-950">{workOrderAnalysis.executiveSummary}</p>
            </div>

            {activeTab === 'grouped-issues' && <>
            <div>
              <h3 className="font-semibold text-gray-900">Recommended priority actions</h3>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {workOrderAnalysis.priorityActions.map((action, index) => (
                  <article key={`${action.title}-${index}`} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded px-2 py-1 text-xs font-semibold ${priorityClasses[action.priority] || priorityClasses.MEDIUM}`}>{formatAnalysisLabel(action.priority)}</span>
                      <h4 className="font-medium text-gray-900">{action.title}</h4>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{action.reason}</p>
                    <div className="mt-3"><WorkOrderLinks ids={action.workOrderIds} orderMap={orderMap} onOpen={openWorkOrderPanel} /></div>
                  </article>
                ))}
                {!workOrderAnalysis.priorityActions.length && <p className="text-sm text-gray-500">No priority actions were identified.</p>}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">Grouped active issues</h3>
              <div className="mt-3 space-y-3">
                {workOrderAnalysis.issueGroups.map((group, index) => (
                  <article key={`${group.title}-${index}`} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded px-2 py-1 text-xs font-semibold ${priorityClasses[group.priority] || priorityClasses.MEDIUM}`}>{formatAnalysisLabel(group.priority)}</span>
                      <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">{formatAnalysisLabel(group.category)}</span>
                      <h4 className="font-medium text-gray-900">{group.title}</h4>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{group.summary}</p>
                    <p className="mt-2 text-sm"><span className="font-medium text-gray-800">Suggested next step:</span> {group.recommendedAction}</p>
                    <div className="mt-3"><WorkOrderLinks ids={group.activeWorkOrderIds} orderMap={orderMap} onOpen={openWorkOrderPanel} /></div>
                  </article>
                ))}
                {!workOrderAnalysis.issueGroups.length && <p className="text-sm text-gray-500">No active issue groups were identified.</p>}
              </div>
            </div>
            </>}

            {activeTab === 'recurring-issues' && <div>
                <h3 className="font-semibold text-gray-900">Recurring issues</h3>
                <div className="mt-3 space-y-3">
                  {workOrderAnalysis.recurringIssues.map((issue, index) => (
                    <article key={`${issue.title}-${index}`} className="rounded-lg border border-gray-200 p-4">
                      <h4 className="font-medium text-gray-900">{issue.title} <span className="text-sm font-normal text-gray-500">({issue.occurrenceCount} occurrences)</span></h4>
                      <p className="mt-2 text-sm text-gray-600">{issue.summary}</p>
                      <div className="mt-3"><WorkOrderLinks ids={issue.workOrderIds} orderMap={orderMap} onOpen={openWorkOrderPanel} /></div>
                    </article>
                  ))}
                  {!workOrderAnalysis.recurringIssues.length && <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">No recurring pattern was identified.</p>}
                </div>
              </div>}
            {activeTab === 'stale' && <div>
                <h3 className="font-semibold text-gray-900">Suggested closure review</h3>
                <p className="mt-1 text-xs text-gray-500">A manager must verify the work before closing or merging anything.</p>
                <div className="mt-3 space-y-3">
                  {workOrderAnalysis.closureCandidates.map(candidate => (
                    <article key={candidate.workOrderId} className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <div className="flex flex-wrap gap-2">
                        <span className={`rounded px-2 py-1 text-xs font-semibold ${confidenceClasses[candidate.confidence] || confidenceClasses.LOW}`}>{formatAnalysisLabel(candidate.confidence)} confidence</span>
                        <span className="rounded bg-white px-2 py-1 text-xs font-medium text-amber-900">{formatAnalysisLabel(candidate.recommendation)}</span>
                      </div>
                      <p className="mt-2 text-sm text-amber-950">{candidate.reason}</p>
                      <div className="mt-3"><WorkOrderLinks ids={[candidate.workOrderId]} orderMap={orderMap} onOpen={openWorkOrderPanel} /></div>
                      {decisionByWorkOrder.get(candidate.workOrderId) ? (
                        <div className={`mt-3 rounded-md p-3 text-xs ${decisionByWorkOrder.get(candidate.workOrderId).applicationStatus === 'APPLIED' ? 'bg-emerald-100 text-emerald-900' : 'bg-red-100 text-red-800'}`}>
                          <div className="font-semibold">Manager decision: {formatAnalysisLabel(decisionByWorkOrder.get(candidate.workOrderId).action)} · {formatAnalysisLabel(decisionByWorkOrder.get(candidate.workOrderId).applicationStatus)}</div>
                          <div className="mt-1">{decisionByWorkOrder.get(candidate.workOrderId).note}</div>
                          <div className="mt-1 opacity-70">Recorded {new Date(decisionByWorkOrder.get(candidate.workOrderId).createdAt).toLocaleString()}</div>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={!workOrderAnalysis.isCurrent || applyingDecision}
                            onClick={() => openDecisionForm(candidate.workOrderId, 'KEEP_OPEN')}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Keep open
                          </button>
                          <button
                            type="button"
                            disabled={!workOrderAnalysis.isCurrent || applyingDecision}
                            onClick={() => openDecisionForm(candidate.workOrderId, 'VERIFY_AND_CLOSE')}
                            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Verified resolved &amp; close
                          </button>
                          <button
                            type="button"
                            disabled={!workOrderAnalysis.isCurrent || applyingDecision || detail.openWorkOrders.length < 2}
                            onClick={() => openDecisionForm(candidate.workOrderId, 'CLOSE_AS_DUPLICATE')}
                            className="rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Close as duplicate
                          </button>
                        </div>
                      )}
                      {decisionForm?.workOrderId === candidate.workOrderId && (
                        <form onSubmit={handleDecision} className="mt-3 rounded-lg border border-amber-300 bg-white p-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">{formatAnalysisLabel(decisionForm.action)}</div>
                          {decisionForm.action === 'CLOSE_AS_DUPLICATE' && (
                            <label className="mt-3 block text-xs font-medium text-gray-700">
                              Canonical work order to keep
                              <select
                                value={decisionForm.duplicateOfWorkOrderId}
                                onChange={event => setDecisionForm(current => ({ ...current, duplicateOfWorkOrderId: event.target.value }))}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              >
                                <option value="">Select a work order</option>
                                {detail.openWorkOrders.filter(order => order.id !== candidate.workOrderId).map(order => (
                                  <option key={order.id} value={order.id}>{order.description?.slice(0, 100) || order.id}</option>
                                ))}
                              </select>
                            </label>
                          )}
                          <label className="mt-3 block text-xs font-medium text-gray-700">
                            Manager note (required)
                            <textarea
                              value={decisionForm.note}
                              onChange={event => setDecisionForm(current => ({ ...current, note: event.target.value }))}
                              rows="3"
                              maxLength="1000"
                              placeholder="What did you verify, and why is this the correct outcome?"
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                          </label>
                          {decisionError && <div className="mt-2 text-xs text-red-700">{decisionError}</div>}
                          <div className="mt-3 flex gap-2">
                            <button type="submit" disabled={applyingDecision} className="rounded-md bg-sky-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-800 disabled:bg-gray-300">{applyingDecision ? 'Applying…' : 'Confirm decision'}</button>
                            <button type="button" onClick={() => { setDecisionForm(null); setDecisionError(''); }} className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200">Cancel</button>
                          </div>
                        </form>
                      )}
                    </article>
                  ))}
                  {!workOrderAnalysis.closureCandidates.length && <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">No active tickets were recommended for closure review.</p>}
                </div>
              </div>}

            <div className="border-t border-gray-100 pt-3 text-xs text-gray-400">
              Generated {new Date(workOrderAnalysis.generatedAt).toLocaleString()} with {workOrderAnalysis.model} from {workOrderAnalysis.activeWorkOrderCount} active and {workOrderAnalysis.completedContextCount} recently completed work orders. Usage: {workOrderAnalysis.inputTokens} input / {workOrderAnalysis.outputTokens} output tokens.
            </div>
          </div>
        )}
      </section>}

      {activeTab === 'active' && <section className="mt-4 overflow-hidden rounded-lg bg-white shadow-sm"><div className="border-b border-gray-200 px-5 py-4"><h2 className="text-lg font-semibold text-gray-900">Open and in-progress work</h2><p className="text-sm text-gray-500">All active work orders, without a date restriction. Select a date or Open to view the ticket without leaving this page.</p></div><WorkOrderTable orders={detail.openWorkOrders} staleIds={staleIds} onOpen={openWorkOrderPanel} /></section>}

      {activeTab === 'completed' && <section className="mt-4 overflow-hidden rounded-lg bg-white shadow-sm">
        <div className="border-b border-gray-200 px-5 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div><h2 className="text-lg font-semibold text-gray-900">Completed work</h2><p className="text-sm text-gray-500">{detail.completedWorkOrderCount} completed in the selected period. Results load one page at a time.</p></div>
            <div className="flex flex-wrap gap-2">{[60, 90, 120, 180].map(days => <button key={days} onClick={() => { setCompletedDays(days); setCompletedPage(1); if (completedWorkLoaded) loadCompletedWork({ page: 1, days }); }} className={`rounded-md px-3 py-1.5 text-sm ${completedDays === days ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{days} days</button>)}</div>
          </div>
          {completedWorkLoaded && (
            <form onSubmit={event => { event.preventDefault(); setCompletedPage(1); loadCompletedWork({ page: 1 }); }} className="mt-4 flex flex-col gap-2 sm:flex-row">
              <input value={completedSearch} onChange={event => setCompletedSearch(event.target.value)} placeholder="Search completed work descriptions" className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm" />
              <select value={completedLimit} onChange={event => { const limit = Number(event.target.value); setCompletedLimit(limit); setCompletedPage(1); loadCompletedWork({ page: 1, limit }); }} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
              </select>
              <button type="submit" className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-800">Search</button>
            </form>
          )}
        </div>
        {!completedWorkLoaded ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-500">Completed tickets are loaded separately to keep the scorecard fast.</p>
            <button type="button" onClick={() => loadCompletedWork({ page: 1 })} className="mt-3 rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-800">View completed work</button>
          </div>
        ) : loadingCompletedWork && !completedWork ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading completed work…</div>
        ) : completedWorkError ? (
          <div className="m-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">Unable to load completed work: {completedWorkError.message}</div>
        ) : (
          <>
            <WorkOrderTable orders={completedWork?.items || []} completed onOpen={openWorkOrderPanel} />
            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
              <span>{completedWork?.total ? `Showing ${(completedWork.page - 1) * completedWork.limit + 1}–${Math.min(completedWork.page * completedWork.limit, completedWork.total)} of ${completedWork.total}` : 'No matching completed work orders'}</span>
              <div className="flex items-center gap-2">
                <button type="button" disabled={!completedWork || completedWork.page <= 1 || loadingCompletedWork} onClick={() => { const page = completedPage - 1; setCompletedPage(page); loadCompletedWork({ page }); }} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
                <span>Page {completedWork?.page || 1} of {Math.max(1, Math.ceil((completedWork?.total || 0) / (completedWork?.limit || completedLimit)))}</span>
                <button type="button" disabled={!completedWork?.hasMore || loadingCompletedWork} onClick={() => { const page = completedPage + 1; setCompletedPage(page); loadCompletedWork({ page }); }} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50">Next</button>
              </div>
            </div>
          </>
        )}
      </section>}

      {activeTab === 'reviews' && <section className="mt-4 rounded-lg bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Guest reviews from the last 180 days</h2>
            <p className="mt-1 text-sm text-gray-500">Includes pending, approved, and declined Streamline reviews. AI analysis is generated only for this property; property-related feedback contributes to the score while company-service feedback is excluded.</p>
          </div>
          <button
            type="button"
            onClick={() => handleClassifyReviews(false)}
            disabled={classifyingReviews || !detail.recentReviews.length}
            className="whitespace-nowrap rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {classifyingReviews ? 'Analyzing reviews…' : 'Analyze reviews with AI'}
          </button>
        </div>
        {analysisResult && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            Analyzed {analysisResult.classifiedCount} review{analysisResult.classifiedCount === 1 ? '' : 's'} with {analysisResult.model}.
            {' '}{analysisResult.skippedCount} already current, {analysisResult.failedCount} failed, and {analysisResult.remainingCount} remaining.
            <span className="block text-xs text-emerald-800">Usage: {analysisResult.inputTokens} input and {analysisResult.outputTokens} output tokens.</span>
          </div>
        )}
        {analysisError && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{analysisError}</div>}
        <div className="mt-4 space-y-3">
          {detail.recentReviews.map(review => (
            <article key={review.id} className="rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-gray-900">{review.title || 'Guest review'}</div>
                <div className="flex flex-wrap items-center justify-end gap-2 text-xs text-gray-500">
                  <span>{formatDate(review.reviewCreatedAt)} · {review.sourceName || 'Streamline'}{review.points != null ? ` · ${review.points} / 5` : ''}</span>
                  <span className={`rounded-full px-2 py-0.5 font-medium ${review.statusId === 2 ? 'bg-emerald-100 text-emerald-800' : review.statusId === 3 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                    {review.statusId === 2 ? 'Approved' : review.statusId === 3 ? 'Declined' : 'Pending'}
                  </span>
                </div>
              </div>
              {review.comments && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{review.comments}</p>}
              {review.aiClassifiedAt ? (
                <div className="mt-3 rounded-md bg-slate-50 p-3">
                  <div className="flex flex-wrap gap-2 text-xs font-medium">
                    <span className="rounded bg-sky-100 px-2 py-1 text-sky-800">{formatAnalysisLabel(review.aiCategory)}</span>
                    <span className={`rounded px-2 py-1 ${sentimentClasses[review.aiSentiment] || sentimentClasses.NEUTRAL}`}>{formatAnalysisLabel(review.aiSentiment)}</span>
                    <span className="rounded bg-violet-100 px-2 py-1 text-violet-800">Severity: {formatAnalysisLabel(review.aiSeverity)}</span>
                    <span className={`rounded px-2 py-1 ${review.aiPropertyRelated ? 'bg-amber-100 text-amber-900' : 'bg-gray-200 text-gray-700'}`}>{review.aiPropertyRelated ? 'Property-related' : 'Excluded from property score'}</span>
                    <span className="rounded bg-gray-200 px-2 py-1 text-gray-700">Impact preview: {Number(review.aiScoreImpact || 0) > 0 ? '+' : ''}{Number(review.aiScoreImpact || 0).toFixed(2)}</span>
                  </div>
                  {review.aiSummary && <p className="mt-2 text-sm font-medium text-gray-800">{review.aiSummary}</p>}
                  {review.aiReasoning && <p className="mt-1 text-xs text-gray-600">{review.aiReasoning}</p>}
                  <div className="mt-2 text-xs text-gray-400">Analyzed {formatDate(review.aiClassifiedAt)} · relevance {Math.round(Number(review.aiRelevance || 0) * 100)}%</div>
                </div>
              ) : review.aiClassificationError ? (
                <div className="mt-3 rounded-md bg-red-50 p-3 text-xs text-red-700">Analysis error: {review.aiClassificationError}</div>
              ) : (
                <div className="mt-3 text-xs text-gray-400">Not analyzed yet</div>
              )}
            </article>
          ))}
          {!detail.recentReviews.length && <div className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-500">No guest reviews have been imported for this period.</div>}
        </div>
      </section>}

      <div className={activeTab === 'owner' ? '' : 'hidden'}>
        <OwnerReportEditor
          propertyId={propertyId}
          property={detail.property}
          completedDays={completedDays}
          onOpenWorkOrder={openWorkOrderPanel}
        />
      </div>
      <WorkOrderDetailSlidePanel
        isOpen={isWorkOrderPanelOpen}
        onClose={closeWorkOrderPanel}
        workOrderId={selectedWorkOrderId}
        onDataChange={handleWorkOrderDataChange}
      />
    </div>
  );
}
